/**
 * publicTagHost — detect when the app is being served on the NEUTRAL,
 * white-label jewellery-tag domain (e.g. https://rosentiquestag.com) rather
 * than the main portal domain (inventique.co.in) or the preview URL.
 *
 * When this returns true, App.js runs the app in "TAG-ONLY MODE": only the
 * public /t/:id QR-scan viewer is mounted; every other path shows a neutral
 * landing page and the portal / login / ERP routes are NEVER rendered. This is
 * what makes a second deployment on the neutral domain safe — typing the bare
 * domain can never expose the management portal.
 *
 * The neutral host is derived from REACT_APP_PUBLIC_TAG_BASE_URL (the same env
 * var the QR codes point at), so no extra per-deployment configuration is
 * needed — the running hostname alone decides the mode.
 */

function normHost(h) {
  return (h || '').trim().toLowerCase().replace(/^www\./, '');
}

// The bare hostname of the configured neutral tag domain ('' when unset).
export function getPublicTagHost() {
  try {
    const base = process.env.REACT_APP_PUBLIC_TAG_BASE_URL || '';
    if (!base) return '';
    const url = base.includes('://') ? new URL(base) : new URL(`https://${base}`);
    return normHost(url.hostname);
  } catch {
    return '';
  }
}

export function isPublicTagHost() {
  if (typeof window === 'undefined') return false;
  // QA override — force tag-only mode on ANY host for verification. This is
  // safe: tag-only mode only RESTRICTS the app to the public viewer + Notes-
  // authed Tag Workspace, it never exposes anything, so the override cannot
  // leak data even on the main domain. We persist it in sessionStorage so the
  // mode survives client-side navigation (e.g. the login → "/" redirect drops
  // the query string) during a verification session.
  try {
    if (new URLSearchParams(window.location.search).get('__tagonly') === '1') {
      sessionStorage.setItem('__tagonly', '1');
      return true;
    }
    if (sessionStorage.getItem('__tagonly') === '1') return true;
  } catch { /* noop */ }
  const target = getPublicTagHost();
  if (!target) return false;
  return normHost(window.location.hostname) === target;
}
